import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Container, Form, Modal, Table } from "react-bootstrap";
import {
  deleteItemCart,
  extendCartSession,
  setResellerCoupon,
} from "../actions/cartActions";
import { RootState } from "../reducers";
import ProductTagsTypes from "./ProductTagsTypes";
import { PencilSquare, TrashFill } from "react-bootstrap-icons";
import Loading from "./Loading";
import { Cart } from "../actions/types";
import parse from "html-react-parser";
import { useNavigate } from "react-router-dom";
import { formatPrice, getLiquidacionItemsCount } from "./utilities";
import { ProductTag } from "../actions/enums";



const CartList = () => {
  const cart = useSelector((state: RootState) => state.cart.cart);
  const totalCart = useSelector((state: RootState) => state.cart.totalCart);
  const itemsCart = useSelector((state: RootState) => state.cart.itemsCart);
  const minimumCart = useSelector((state: RootState) => state.cart.minimumCart);
  const minimumPurchaseAmount = useSelector((state: RootState) => state.cart.minimumPurchaseAmount);
  const textPages = useSelector((state: RootState) => state.shop.textPages);
  const isLoading = useSelector((state: RootState) => state.cart.isLoading);
  const frequentUserCoupon = useSelector(
    (state: RootState) => state.cart.frequentUserCoupon,
  );
  const resellerCoupon = useSelector(
    (state: RootState) => state.cart.resellerCoupon,
  );
  const resellerPercentageDiscount = useSelector(
    (state: RootState) => state.cart.resellerPercentageDiscount,
  );
  const resellerMinAmountDiscount = useSelector(
    (state: RootState) => state.cart.resellerMinAmountDiscount,
  );
  const appliedResellerCoupon = useSelector(
    (state: RootState) => state.cart.appliedResellerCoupon,
  );
  const navigate: any = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [isFrequentUser, setIsFrequentUser] = useState(false);
  const [isResellerDiscount, setIsResellerDiscount] = useState(
    appliedResellerCoupon !== null && appliedResellerCoupon === resellerCoupon,
  );
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => {
    dispatch(extendCartSession());
    setShow(true);
  };

  const dispatch: any = useDispatch();

  useEffect(() => {
    if (coupon === frequentUserCoupon) {
      setIsFrequentUser(true);
    } else {
      setIsFrequentUser(false);
    }
  }, [coupon, frequentUserCoupon]);

  const liquidacionItemsCount = useMemo(
    () => getLiquidacionItemsCount(cart as Cart[]),
    [cart],
  );

  const hasLiquidacionBlock = useMemo(
    () => liquidacionItemsCount >= minimumCart,
    [liquidacionItemsCount, minimumCart],
  );

  useEffect(() => {
    const totalCartNumber = Number(totalCart);
    const resellerMinAmountNumber = Number(resellerMinAmountDiscount);

    const wouldApplyResellerDiscount =
      (coupon === resellerCoupon && coupon !== "") ||
      (totalCartNumber > resellerMinAmountNumber &&
        resellerMinAmountNumber > 0);

    // Aplicar descuento de reseller si:
    // 1. El cupón ingresado coincide con el cupón de reseller, O
    // 2. El total del carrito es mayor al monto mínimo de descuento
    // EXCEPCIÓN: si hay productos de LIQUIDACIÓN que alcanzan el mínimo,
    // no se aplica el descuento reseller.
    if (hasLiquidacionBlock) {
      setIsResellerDiscount(false);
      dispatch(setResellerCoupon(null));
      return;
    }

    setIsResellerDiscount(wouldApplyResellerDiscount);

    // Guardar el cupón aplicado en el estado global
    if (wouldApplyResellerDiscount) {
      const appliedCoupon =
        coupon === resellerCoupon && coupon !== "" ? coupon : null;
      dispatch(setResellerCoupon(appliedCoupon));
    } else {
      dispatch(setResellerCoupon(null));
    }
  }, [
    coupon,
    resellerCoupon,
    totalCart,
    resellerMinAmountDiscount,
    dispatch,
    hasLiquidacionBlock,
  ]);

  const onDeleteItemCart = (id: string) => {
    dispatch(deleteItemCart(id));
  };

  const getIsHotSale = (item: Cart) =>
    item.options.product.types?.some((type) => type.name === ProductTag.HOT_SALE);

  // Funciones para calcular precios
  const getSubtotal = () => {
    return Number(totalCart);
  };

  const getDiscountAmount = () => {
    if (isResellerDiscount) {
      return Math.round(
        (Number(totalCart) * Number(resellerPercentageDiscount)) / 100,
      );
    }
    return 0;
  };

  const getFinalTotal = () => {
    return getSubtotal() - getDiscountAmount();
  };

  return (
    <Container>
      {!isFrequentUser && minimumCart > itemsCart && (
        <h4 className="text-center">
          El mínimo de compra son {minimumCart} productos.
        </h4>
      )}
      {!isFrequentUser && minimumPurchaseAmount > 0 && Number(totalCart) < minimumPurchaseAmount && (
        <h4 className="text-center">
          El monto mínimo de compra es ${minimumPurchaseAmount}.
        </h4>
      )}

      {isLoading && <Loading />}

      {!isLoading && itemsCart > 0 && (
        <div>
          <Table bordered className="text-center align-middle">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Precio</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <>
                {(cart as Cart[]).map((item: Cart, index: number) => (
                  <tr key={index}>
                    <td>
                      <p>
                        <img
                          alt={item.name}
                          className="rounded"
                          src={item.options.img}
                          height="100"
                          onClick={() => navigate("/product/" + item.id)}
                        />
                      </p>
                      {item.name}{" "}
                      <ProductTagsTypes
                        excludeTypes={[ProductTag.WEB]}
                        types={item.options.product.types}
                      />
                      {getIsHotSale(item) && (
                        <p className="small m-1">
                          *Los productos en Hot Sale solo tienen cambio por
                          falla.
                        </p>
                      )}
                    </td>
                    <td>
                      <Button
                        className="btn-light mb-2"
                        onClick={() => navigate("/product/" + item.id)}
                      >
                        <b>{item.qty}</b>
                        <PencilSquare className="ms-2" />
                      </Button>
                      <Button
                        className="btn-light mb-2"
                        onClick={() => navigate("/product/" + item.id)}
                      >
                        ${formatPrice(item.price)} c/u
                      </Button>
                    </td>
                    <td>
                      <Button className="btn-light mb-2">
                        ${formatPrice(item.qty * item.price)}
                      </Button>
                      <Button
                        className="btn-danger mb-2"
                        onClick={() => onDeleteItemCart(item.rowId)}
                      >
                        <TrashFill />
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} className="text-end">
                    <h6>Subtotal:</h6>
                  </td>
                  <td>
                    <h6>${formatPrice(getSubtotal())}</h6>
                  </td>
                </tr>
                {isResellerDiscount && (
                  <tr>
                    <td colSpan={2} className="text-end">
                      <h6>Descuento ({resellerPercentageDiscount}%):</h6>
                    </td>
                    <td>
                      <h6 className="text-success">-${formatPrice(getDiscountAmount())}</h6>
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={2} className="text-end">
                    <h5>Total:</h5>
                  </td>
                  <td>
                    <h5>${formatPrice(getFinalTotal())}</h5>
                  </td>
                </tr>
              </>
            </tbody>
          </Table>

          {hasLiquidacionBlock &&
            ((coupon === resellerCoupon && coupon !== "") ||
              (Number(totalCart) > Number(resellerMinAmountDiscount) &&
                Number(resellerMinAmountDiscount) > 0)) && (
              <div className="alert alert-warning text-center mb-3">
                <strong>⚠️ Atención:</strong> Tu carrito contiene{" "}
              <strong>
                {liquidacionItemsCount} producto(s) de LIQUIDACIÓN
              </strong>{" "}
               que alcanzan el mínimo de compra ({minimumCart} productos{minimumPurchaseAmount > 0 ? `, $${minimumPurchaseAmount} monto mínimo` : ""}). Los productos de
              LIQUIDACIÓN{" "}
              <strong>no son compatibles con el descuento de revendedor</strong>
              . Por favor, retirá esos productos del carrito y realizá una
              compra aparte para poder aplicar el descuento.
            </div>
          )}

          {!hasLiquidacionBlock && isResellerDiscount && (
            <div className="alert alert-success text-center mb-3">
              {coupon === resellerCoupon && coupon !== ""
                ? `Se aplicará un descuento del ${resellerPercentageDiscount}% para pagos en efectivo/transferencia.`
                : `Se aplicará un descuento del ${resellerPercentageDiscount}% para compras mayores a $${resellerMinAmountDiscount} para pagos en efectivo/transferencia.`}
            </div>
          )}

          <div className="col-md-3 float-end text-end mb-4">
            <Form.Group className="mb-2">
              <Form.Control
                name="coupon"
                size="lg"
                placeholder="Código vendedor"
                defaultValue={coupon}
                onChange={(element) => setCoupon(element.target.value)}
              />
            </Form.Group>
            <Button
              variant="secondary btn-lg mb-2"
              onClick={() => navigate("/")}
            >
              Agregar más productos
            </Button>
            {(isFrequentUser || (itemsCart >= minimumCart && (minimumPurchaseAmount <= 0 || Number(totalCart) >= minimumPurchaseAmount))) && (
              <Button variant="success btn-lg ms-1 mb-2" onClick={handleShow}>
                Finalizar compra
              </Button>
            )}
          </div>
        </div>
      )}
      {!isLoading && itemsCart === 0 && (
        <div className="text-center">
          <Button
            variant="secondary btn-lg mb-2"
            onClick={() => {
              navigate("/");
            }}
          >
            Inicio
          </Button>
        </div>
      )}
      <Modal show={show} onHide={handleClose} scrollable={true} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Antes de comprar leé ésta información</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {textPages?.termsAndConditions && parse(textPages.termsAndConditions)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary btn-lg" onClick={handleClose}>
            Volver
          </Button>
          <Button
            variant="success btn-lg"
            onClick={() => navigate("/checkout")}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
export default CartList;
